import React from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, FormControlLabel, InputLabel, ListItemText,
  LinearProgress, MenuItem, Select, Stack, Step, StepLabel, Stepper,
  TextField, Typography,
} from '@mui/material';
import { StargraveLeader, StargraveLeaderRole, StargraveStat } from '../../types';
import {
  stargraveBackgrounds, stargraveLeaderRules, stargravePowers,
} from '../../data/stargrave/coreRules';
import { PowerCard } from '../shared/PowerCard';
import { useStargraveEquipment } from '../../stores/stargraveCustomStore';
import { buildStargraveLeader } from '../../game/stargraveLeaderRules';

interface StargraveLeaderModalProps {
  open: boolean;
  role: StargraveLeaderRole;
  onClose: () => void;
  onSave: (leader: StargraveLeader) => void;
  initialLeader?: StargraveLeader;
  mode?: 'create' | 'edit';
  /** Step to open on when editing (e.g. 2 = Powers). */
  initialStep?: number;
  /** Show only the Powers step (no stepper); saving happens from the last power page. */
  powersOnly?: boolean;
}

const statLabels: Record<StargraveStat, string> = {
  move: 'Move', fight: 'Fight', shoot: 'Shoot', armour: 'Armour', will: 'Will', health: 'Health',
};

const STEPS = ['Details', 'Stats', 'Powers', 'Gear'];

export const StargraveLeaderModal: React.FC<StargraveLeaderModalProps> = ({ open, role, onClose, onSave, initialLeader, mode = 'create', initialStep = 0, powersOnly = false }) => {
  const rules = stargraveLeaderRules[role];
  const roleLabel = role === 'captain' ? 'Captain' : 'First Mate';
  const [activeStep, setActiveStep] = React.useState(0);
  const [powerPage, setPowerPage] = React.useState(0);
  const [corePowerCount, setCorePowerCount] = React.useState<number>(rules.corePowerRange[0]);
  const [name, setName] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [backgroundId, setBackgroundId] = React.useState('');
  const [statChoices, setStatChoices] = React.useState<StargraveStat[]>([]);
  const [powerIds, setPowerIds] = React.useState<string[]>([]);
  const [improvedPowerIds, setImprovedPowerIds] = React.useState<string[]>([]);
  const [gearIds, setGearIds] = React.useState<string[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const stargraveEquipment = useStargraveEquipment();

  const background = stargraveBackgrounds.find((candidate) => candidate.id === backgroundId);
  const corePowers = background
    ? stargravePowers.filter((power) => background.corePowerIds.includes(power.id))
    : [];
  const nonCorePowers = background
    ? stargravePowers.filter((power) => !background.corePowerIds.includes(power.id))
    : [];
  const powerNameById = new Map(stargravePowers.map((power) => [power.id, power.name]));
  const equipmentNameById = new Map(stargraveEquipment.map((item) => [item.id, item.name]));
  const occupiedSlots = gearIds.reduce(
    (total, id) => total + (stargraveEquipment.find((item) => item.id === id)?.gearSlots ?? 0), 0
  );

  React.useEffect(() => {
    if (!open) return;
    if (!initialLeader) {
      setActiveStep(0); setPowerPage(0); setCorePowerCount(rules.corePowerRange[0]);
      setName(''); setNotes(''); setBackgroundId(''); setStatChoices([]); setPowerIds([]);
      setImprovedPowerIds([]); setGearIds([]); setError(null);
      return;
    }
    const selectedBackground = stargraveBackgrounds.find((candidate) => candidate.id === initialLeader.backgroundId);
    const statChoicesFromLeader = selectedBackground
      ? selectedBackground.choice.options
        .filter((stat) => initialLeader.stats[stat] > rules.baseStats[stat] + (selectedBackground.fixedModifiers[stat] ?? 0))
        .slice(0, selectedBackground.choice.count)
      : [];
    const coreCount = selectedBackground
      ? initialLeader.powers.filter((power) => selectedBackground.corePowerIds.includes(power.powerId)).length
      : rules.corePowerRange[0];
    const inferredImprovements = selectedBackground
      ? initialLeader.powers.filter((selection) => {
        const power = stargravePowers.find((candidate) => candidate.id === selection.powerId);
        if (!power) return false;
        const modifier = selectedBackground.corePowerIds.includes(selection.powerId)
          ? rules.coreActivationModifier
          : rules.nonCoreActivationModifier;
        return selection.activation < power.activation + modifier;
      }).slice(0, rules.activationImprovements).map((selection) => selection.powerId)
      : [];
    setActiveStep(initialLeader ? initialStep : 0); setPowerPage(0); setCorePowerCount(coreCount);
    setName(initialLeader.name); setNotes(initialLeader.notes ?? ''); setBackgroundId(initialLeader.backgroundId); setStatChoices(statChoicesFromLeader);
    setPowerIds(initialLeader.powers.map((power) => power.powerId));
    setImprovedPowerIds(inferredImprovements); setGearIds(initialLeader.gearIds); setError(null);
  }, [open, initialLeader, initialStep, role, rules.baseStats, rules.corePowerRange]);

  const reset = () => {
    setActiveStep(0);
    setPowerPage(0);
    setCorePowerCount(rules.corePowerRange[0]);
    setName(''); setNotes(''); setBackgroundId(''); setStatChoices([]); setPowerIds([]);
    setImprovedPowerIds([]); setGearIds([]); setError(null);
  };
  const handleClose = () => { reset(); onClose(); };
  const handleSave = () => {
    try {
      const leader = buildStargraveLeader({ role, name, backgroundId, statChoices, powerIds, improvedPowerIds, gearIds });
      onSave(initialLeader ? { ...leader, id: initialLeader.id, notes } : { ...leader, notes });
      handleClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : `Unable to ${mode === 'edit' ? 'edit' : 'create'} ${roleLabel.toLowerCase()}.`);
    }
  };
  const handleNext = () => {
    setError(null);
    if (activeStep === 0) {
      if (!name.trim()) return setError(`${roleLabel} name is required.`);
      if (!background) return setError('Select a background.');
    }
    if (activeStep === 1 && background && statChoices.length !== background.choice.count) {
      return setError(`Choose ${background.choice.count} stat improvement${background.choice.count === 1 ? '' : 's'}.`);
    }
    if (activeStep === 2) {
      const isImprovementPage = powerPage === rules.startingPowers;
      if (!isImprovementPage && !powerIds[powerPage]) return setError('Select a power to continue.');
      if (isImprovementPage && improvedPowerIds.length !== rules.activationImprovements) {
        return setError(`Choose exactly ${rules.activationImprovements} powers to improve.`);
      }
      const lastPowerPage = rules.startingPowers + (rules.activationImprovements > 0 ? 1 : 0) - 1;
      if (powerPage < lastPowerPage) {
        setPowerPage((current) => current + 1);
        return;
      }
      if (powersOnly) {
        handleSave();
        return;
      }
    }
    setActiveStep((current) => Math.min(current + 1, STEPS.length - 1));
  };
  const handleBack = () => {
    setError(null);
    if (activeStep === 2 && powerPage > 0) {
      setPowerPage((current) => current - 1);
      return;
    }
    if (powersOnly) return;
    setActiveStep((current) => Math.max(current - 1, 0));
  };
  const toggleStat = (stat: StargraveStat) => {
    if (!background) return;
    setStatChoices((current) => current.includes(stat)
      ? current.filter((candidate) => candidate !== stat)
      : current.length < background.choice.count ? [...current, stat] : current);
  };
  const selectPowerForPage = (powerId: string) => {
    setPowerIds((current) => {
      const next = [...current];
      next[powerPage] = powerId;
      return next;
    });
    setImprovedPowerIds((current) => current.filter((id) => id !== powerIds[powerPage]));
  };

  const isPowerChoicePage = powerPage < rules.startingPowers;
  const isCorePowerPage = isPowerChoicePage && powerPage < corePowerCount;
  const currentPowerId = isPowerChoicePage ? powerIds[powerPage] : undefined;
  const currentPower = stargravePowers.find((power) => power.id === currentPowerId);
  const selectedOnOtherPages = new Set(powerIds.filter((_, index) => index !== powerPage));
  const availablePowers = (isCorePowerPage ? corePowers : nonCorePowers)
    .filter((power) => !selectedOnOtherPages.has(power.id));
  const powerPageCount = rules.startingPowers + (rules.activationImprovements > 0 ? 1 : 0);
  const powerPageLabel = isPowerChoicePage
    ? `${isCorePowerPage ? 'Core' : 'Non-core'} Power ${powerPage + 1} of ${rules.startingPowers}`
    : 'Activation Improvements';
  const powerPageCanAdvance = isPowerChoicePage
    ? Boolean(currentPowerId)
    : improvedPowerIds.length === rules.activationImprovements;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{powersOnly ? `Edit Powers — ${name}` : `${mode === 'edit' ? 'Edit' : 'Create'} ${roleLabel}`}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        {!powersOnly && <Stepper activeStep={activeStep} alternativeLabel>
          {STEPS.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
        </Stepper>}
        {error && <Alert severity="error">{error}</Alert>}

        {activeStep === 0 && <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label={`${roleLabel} Name`} value={name} onChange={(event) => setName(event.target.value)} required />
          <TextField label="Notes" value={notes} onChange={(event) => setNotes(event.target.value)} multiline minRows={2} />
          <FormControl fullWidth>
            <InputLabel id={`${role}-background-label`}>Background</InputLabel>
            <Select labelId={`${role}-background-label`} value={backgroundId} label="Background" onChange={(event) => {
              setBackgroundId(event.target.value); setStatChoices([]); setPowerIds([]); setImprovedPowerIds([]);
              setPowerPage(0); setCorePowerCount(rules.corePowerRange[0]);
            }}>
              {stargraveBackgrounds.map((candidate) => <MenuItem key={candidate.id} value={candidate.id}>{candidate.name}</MenuItem>)}
            </Select>
          </FormControl>
          {background && <Typography variant="body2" color="text.secondary">
            Core powers: {background.corePowerIds.map((id) => powerNameById.get(id)).join(', ')}
          </Typography>}
        </Box>}

        {activeStep === 1 && background && <Box>
          <Typography variant="subtitle2">Choose {background.choice.count} stat improvement{background.choice.count === 1 ? '' : 's'}</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {background.choice.options.map((stat) => <FormControlLabel key={stat}
              control={<Checkbox checked={statChoices.includes(stat)} onChange={() => toggleStat(stat)} />}
              label={statLabels[stat]} />)}
          </Box>
        </Box>}

        {activeStep === 2 && <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{powerPageLabel}</Typography>
            {powerPage === 0 && <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel id={`${role}-core-count-label`}>Core Powers</InputLabel>
              <Select labelId={`${role}-core-count-label`} label="Core Powers" value={corePowerCount}
                onChange={(event) => {
                  setCorePowerCount(Number(event.target.value));
                  setPowerIds([]);
                  setImprovedPowerIds([]);
                }}>
                {rules.corePowerRange.map((count) => <MenuItem key={count} value={count}>{count} core / {rules.startingPowers - count} non-core</MenuItem>)}
              </Select>
            </FormControl>}
          </Stack>
          <LinearProgress variant="determinate" value={((powerPage + 1) / powerPageCount) * 100} />

          {isPowerChoicePage ? <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {availablePowers.map((power) => <Chip key={power.id} label={power.name} clickable
                color={currentPowerId === power.id ? 'primary' : 'default'}
                variant={currentPowerId === power.id ? 'filled' : 'outlined'}
                onClick={() => selectPowerForPage(power.id)} />)}
            </Box>
            {currentPower ? <Box sx={{ minHeight: 180 }}>
              <PowerCard
                name={currentPower.name}
                activation={currentPower.activation + (isCorePowerPage ? rules.coreActivationModifier : rules.nonCoreActivationModifier)}
                strain={currentPower.strain}
                timing={currentPower.timing}
                summary={currentPower.summary}
                hints={[isCorePowerPage ? 'Core' : 'Non-core']}
              />
            </Box> : <Typography color="text.secondary" sx={{ textAlign: 'center', py: 6, minHeight: 180 }}>
              Select a power to view details.
            </Typography>}
          </> : <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Choose {rules.activationImprovements} different powers to reduce their activation number by 1.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {powerIds.map((id) => <Chip key={id} label={powerNameById.get(id)} clickable
                color={improvedPowerIds.includes(id) ? 'primary' : 'default'}
                variant={improvedPowerIds.includes(id) ? 'filled' : 'outlined'}
                onClick={() => setImprovedPowerIds((current) => current.includes(id)
                  ? current.filter((candidate) => candidate !== id)
                  : current.length < rules.activationImprovements ? [...current, id] : current)} />)}
            </Box>
          </Box>}
        </Box>}

        {activeStep === 3 && (
        <FormControl fullWidth>
          <InputLabel id={`${role}-gear-label`}>Starting Gear</InputLabel>
          <Select multiple labelId={`${role}-gear-label`} value={gearIds} label="Starting Gear"
            onChange={(event) => setGearIds(event.target.value as string[])}
            renderValue={(selected) => selected.map((id) => equipmentNameById.get(id)).join(', ')}>
            {stargraveEquipment.map((item) => <MenuItem key={item.id} value={item.id}>
              <Checkbox checked={gearIds.includes(item.id)} />
              <ListItemText primary={item.name} secondary={`${item.gearSlots} slot${item.gearSlots === 1 ? '' : 's'}`} />
            </MenuItem>)}
          </Select>
          <Chip size="small" label={`${occupiedSlots} / ${rules.gearSlots} slots`}
            color={occupiedSlots > rules.gearSlots ? 'error' : 'default'} sx={{ mt: 1, alignSelf: 'flex-start' }} />
        </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleBack} disabled={powersOnly ? powerPage === 0 : activeStep === 0}>Back</Button>
        {(powersOnly ? powerPage === powerPageCount - 1 : activeStep === STEPS.length - 1)
          ? <Button variant="contained" onClick={powersOnly ? handleNext : handleSave} disabled={powersOnly && !powerPageCanAdvance}>{mode === 'edit' ? 'Save Changes' : `Create ${roleLabel}`}</Button>
          : <Button variant="contained" onClick={handleNext} disabled={activeStep === 2 && !powerPageCanAdvance}>Next</Button>}
      </DialogActions>
    </Dialog>
  );
};
